import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { cn } from '../../lib/utils';
import { SlideCanvas } from '../canvas';
import { IconChevronLeft, IconChevronRight, IconDownload, IconVideo } from '../shared/icons';
import { BootSequence } from './BootSequence';

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

// Human-friendly stage messages for worker pipeline stages
const STAGE_LABELS = {
  downloading_source: 'Downloading video from cloud...',
  creating_background: 'Creating background...',
  preparing_video: 'Preparing video...',
  preparing_compositing: 'Preparing for compositing...',
  compositing: 'Compositing video...',
  uploading_result: 'Uploading to cloud...',
  finalizing: 'Finalizing...',
  downloading: 'Downloading result...',
  preparing: 'Starting...',
  complete: 'Complete!',
};

/** Given progress data, return a clean stage message */
function getStageMessage(progress) {
  if (!progress) return 'Starting...';
  if (progress.stage_message && !STAGE_LABELS[progress.stage_message]) {
    return progress.stage_message;
  }
  return STAGE_LABELS[progress.stage] || progress.stage_message || 'Processing...';
}

/**
 * Preview panel with slide canvas inside a hardware-style screen bezel.
 * Buttons styled as instrument buttons in recessed containers.
 */
export const PreviewPanel = forwardRef(function PreviewPanel(
  {
    activeSlide,
    activeIndex,
    totalSlides,
    onPrev,
    onNext,
    onExport,
    onExportJpeg,
    onExportVideo,
    isExporting,
    isExportingJpeg,
    isExportingVideo,
    videoExportProgress,
    exportSafe,
    exportError,
    onDismissExportError,
    template,
    logoEnabled,
    logoUrl,
    logoTitle,
    logoSubtitle,
    logoSize,
    logoIsDark,
    onElementClick,
    editingField,
    onEditChange,
    onEndEdit,
    onBackgroundClick,
    onNudgeEnd,
    onReorder,
  },
  ref
) {
  const previewWrapRef = useRef(null);
  const exportNodeRef = useRef(null);
  const [scale, setScale] = useState(0.4);
  const [entering, setEntering] = useState(false);
  const prevIndexRef = useRef(activeIndex);

  const hasVideoWidget = activeSlide?.videoWidget?.src && activeSlide?.videoWidget?.duration;
  const videoDuration = activeSlide?.videoWidget?.duration;

  const encodingSpeed = videoExportProgress?.encoding_speed || 0;
  const encodingFps = videoExportProgress?.encoding_fps || videoExportProgress?.fps || 0;

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useImperativeHandle(ref, () => ({
    getExportNode: () => exportNodeRef.current,
  }));

  useEffect(() => {
    if (prevIndexRef.current !== activeIndex) {
      setEntering(true);
      const raf = requestAnimationFrame(() => setEntering(false));
      prevIndexRef.current = activeIndex;
      return () => cancelAnimationFrame(raf);
    }
  }, [activeIndex]);

  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el) return;

    const compute = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      // "cover" mode — fill the screen completely, overflow:hidden clips tiny excess
      const s = Math.max(w / 1080, h / 1350);
      setScale(clamp(s, 0.12, 1));
    };

    compute();
    const ro = new ResizeObserver(() => compute());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* ─── Controls bar above bezel ─── */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="device-section-label" style={{ fontSize: '10px', letterSpacing: '1.5px' }}>
          SLIDE {String(totalSlides ? activeIndex + 1 : 0).padStart(2, '0')} / {String(totalSlides || 0).padStart(2, '0')}
        </div>
        <div className="flex items-center gap-2">
          {/* ─── Export buttons in recessed container ─── */}
          <div className="btn-instrument-group">
            {hasVideoWidget ? (
              <button
                onClick={onExportVideo}
                disabled={isExportingVideo || isExporting || isExportingJpeg || !activeSlide}
                className={cn(
                  'btn-instrument btn-instrument-purple',
                  isExportingVideo && 'btn-instrument-active'
                )}
                aria-label="Export Video"
                title={`Export as video (${formatDuration(videoDuration)})`}
              >
                <IconVideo className="h-3.5 w-3.5" />
                {isExportingVideo ? 'WAIT' : 'MP4'}
              </button>
            ) : (
              <>
                <button
                  onClick={onExport}
                  disabled={isExporting || isExportingJpeg || isExportingVideo || !activeSlide}
                  className={cn(
                    'btn-instrument btn-instrument-green',
                    isExporting && 'btn-instrument-active'
                  )}
                  aria-label="Export PNG"
                  title="Export as PNG (full quality, supports transparency)"
                >
                  <IconDownload className="h-3 w-3" />
                  {isExporting ? 'WAIT' : 'PNG'}
                </button>
                <button
                  onClick={onExportJpeg}
                  disabled={isExporting || isExportingJpeg || isExportingVideo || !activeSlide}
                  className={cn(
                    'btn-instrument btn-instrument-pink',
                    isExportingJpeg && 'btn-instrument-active'
                  )}
                  aria-label="Export JPEG"
                  title="Export as JPEG (lightweight, optimized for Instagram)"
                >
                  <IconDownload className="h-3 w-3" />
                  {isExportingJpeg ? 'WAIT' : 'JPG'}
                </button>
              </>
            )}
          </div>

          {/* ─── Navigation buttons in recessed container ─── */}
          <div className="btn-instrument-group">
            <button
              onClick={onPrev}
              className="btn-instrument"
              aria-label="Previous slide"
              disabled={!activeSlide}
            >
              <IconChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={onNext}
              className="btn-instrument"
              aria-label="Next slide"
              disabled={!activeSlide}
            >
              <IconChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ═══ Screen Bezel ═══ */}
      <div className="screen-bezel">
        <div className="screen-container relative">
          {/* Grid overlay */}
          <div className="screen-grid" />

          {/* Preview canvas area */}
          <div
            ref={previewWrapRef}
            className="absolute inset-0 z-[2]"
          >
            {!activeSlide ? (
              <BootSequence />
            ) : (
              <>
                {/* Fade-in overlay when boot finishes */}
                <div
                  className="absolute inset-0 bg-black z-[3] pointer-events-none"
                  style={{
                    animation: 'boot-fade-out 0.6s ease forwards',
                  }}
                />
                <style>{`
                  @keyframes boot-fade-out {
                    from { opacity: 1; }
                    to { opacity: 0; }
                  }
                `}</style>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className={cn(
                      'transition-opacity duration-150 ease-out',
                      entering ? 'opacity-85' : 'opacity-100'
                    )}
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: `translate(-50%, -50%) scale(${scale})`,
                      transformOrigin: 'center',
                    }}
                  >
                  <div ref={exportNodeRef}>
                    <SlideCanvas
                      data={activeSlide}
                      index={activeIndex}
                      exportSafe={exportSafe}
                      template={template}
                      logoEnabled={logoEnabled}
                      logoUrl={logoUrl}
                      logoTitle={logoTitle}
                      logoSubtitle={logoSubtitle}
                      logoSize={logoSize}
                      logoIsDark={logoIsDark}
                      onElementClick={onElementClick}
                      editingField={editingField}
                      onEditChange={onEditChange}
                      onEndEdit={onEndEdit}
                      onBackgroundClick={onBackgroundClick}
                      scale={scale}
                      onNudgeEnd={onNudgeEnd}
                      onReorder={onReorder}
                    />
                  </div>
                </div>
              </div>
              </>
            )}
          </div>

          {/* ─── Export progress overlays (inside screen) ─── */}
          
          {/* PNG progress */}
          {isExporting && (
            <div className="absolute inset-0 rounded-lg bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center z-10">
              <div className="relative">
                <div className="w-14 h-14 border-3 border-white/20 border-t-emerald-400 rounded-full animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-7 h-7 bg-emerald-400/20 rounded-full animate-pulse" />
                </div>
              </div>
              <div className="mt-4 text-white/90 font-bold text-sm font-mono-ibm tracking-wide">RENDERING PNG…</div>
              <div className="mt-1.5 text-xs font-mono-ibm" style={{ color: '#666' }}>THIS MAY TAKE A FEW SECONDS</div>
            </div>
          )}

          {/* JPEG progress */}
          {isExportingJpeg && (
            <div className="absolute inset-0 rounded-lg bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center z-10">
              <div className="relative">
                <div className="w-14 h-14 border-3 border-white/20 border-t-pink-400 rounded-full animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-7 h-7 bg-pink-400/20 rounded-full animate-pulse" />
                </div>
              </div>
              <div className="mt-4 text-white/90 font-bold text-sm font-mono-ibm tracking-wide">RENDERING JPEG…</div>
              <div className="mt-1.5 text-xs font-mono-ibm" style={{ color: '#666' }}>OPTIMIZED FOR INSTAGRAM</div>
            </div>
          )}

          {/* Video Export progress */}
          {isExportingVideo && (
            <div className="absolute inset-0 rounded-lg bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center z-10">
              <div className="relative">
                <div className="w-14 h-14 border-3 border-white/20 border-t-purple-400 rounded-full animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <IconVideo className="w-5 h-5 text-purple-400 animate-pulse" />
                </div>
              </div>
              
              <div className="mt-4 text-white/90 font-bold text-sm font-mono-ibm tracking-wide">
                RENDERING VIDEO...
              </div>
              
              {/* Rendering power indicator */}
              {encodingSpeed > 0.1 && (
                <div className="mt-2.5 flex items-center gap-2 px-3 py-1 rounded-md bg-purple-500/20 border border-purple-400/30">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-purple-300 text-xs font-bold font-mono-ibm tracking-wide">
                    {encodingSpeed.toFixed(1)}X ENCODER
                  </span>
                </div>
              )}
              
              {/* Step indicator dots */}
              {videoExportProgress?.step_number > 0 && (
                <div className="mt-3 flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5].map((step) => (
                    <div
                      key={step}
                      className={cn(
                        "w-1.5 h-1.5 rounded-full transition-all duration-300",
                        step < videoExportProgress.step_number
                          ? "bg-purple-400"
                          : step === videoExportProgress.step_number
                          ? "bg-purple-400 ring-2 ring-purple-400/30 ring-offset-1 ring-offset-transparent"
                          : "bg-white/20"
                      )}
                    />
                  ))}
                </div>
              )}
              
              {/* Stage message */}
              <div className="mt-3 text-white/80 font-medium text-xs font-mono-ibm tracking-wide">
                {getStageMessage(videoExportProgress)}
              </div>
              
              {/* Progress bar for Step 2 (preparing video / proxy) */}
              {videoExportProgress?.stage === 'preparing_video' && (
                <div className="mt-3 w-48">
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={cn(
                        "h-full bg-purple-400 rounded-full",
                        !videoExportProgress?.proxy_needed
                          ? "transition-all duration-[2000ms] ease-out"
                          : "transition-all duration-300"
                      )}
                      style={{ 
                        width: videoExportProgress?.proxy_needed 
                          ? `${Math.max(5, ((videoExportProgress.proxy_time || 0) / (videoExportProgress.proxy_duration || 1)) * 100)}%`
                          : '100%'
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Step 3: Preparing for compositing */}
              {videoExportProgress?.stage === 'preparing_compositing' && (
                <div className="mt-3 text-xs font-mono-ibm" style={{ color: '#666' }}>
                  THIS MAY TAKE A MOMENT...
                </div>
              )}
              
              {/* Progress bar for Step 4 (compositing) */}
              {videoExportProgress?.stage === 'compositing' && (
                <div className="mt-3 w-48">
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-400 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.max(5, ((videoExportProgress?.encoded_time || 0) / (videoExportProgress?.total_duration || 1)) * 100)}%`
                      }}
                    />
                  </div>
                  {encodingSpeed > 0.1 && (
                    <div className="mt-1 text-xs text-center tabular-nums font-mono-ibm" style={{ color: '#666' }}>
                      {videoExportProgress?.encoded_time?.toFixed(1) || 0}s / {videoExportProgress?.total_duration?.toFixed(1) || '?'}s
                      {encodingFps > 0.1 ? ` • ${encodingFps.toFixed(1)} fps` : ''}
                    </div>
                  )}
                </div>
              )}
              
              {/* Step 5: Download progress */}
              {(videoExportProgress?.stage === 'downloading' || videoExportProgress?.stage === 'finalizing') && (
                <div className="mt-3 w-48">
                  {videoExportProgress?.download_percent > 0 ? (
                    <>
                      <div className="text-xs text-center mb-1 font-mono-ibm" style={{ color: '#ccc' }}>
                        {videoExportProgress.download_percent || 0}%
                      </div>
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-400 rounded-full transition-all duration-150"
                          style={{ width: `${videoExportProgress.download_percent || 0}%` }}
                        />
                      </div>
                      <div className="mt-1.5 text-xs text-center font-mono-ibm" style={{ color: '#666' }}>
                        {videoExportProgress.downloaded_formatted || '0 B'} / {videoExportProgress.total_bytes_formatted || '...'}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-center font-mono-ibm" style={{ color: '#666' }}>
                      STARTING DOWNLOAD...
                    </div>
                  )}
                </div>
              )}
              
              {/* Fallback for initial state */}
              {(!videoExportProgress || videoExportProgress?.stage === 'preparing') && (
                <div className="mt-3 text-xs font-mono-ibm" style={{ color: '#666' }}>
                  {formatDuration(videoDuration)} DURATION
                </div>
              )}
            </div>
          )}

          {/* ─── Export error overlay ─── */}
          {exportError && !isExporting && !isExportingJpeg && !isExportingVideo && (
            <div
              className="absolute inset-0 rounded-lg bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center z-10 cursor-pointer"
              onClick={onDismissExportError}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') onDismissExportError?.(); }}
            >
              {/* Error icon */}
              <div className="w-14 h-14 rounded-full border-2 border-red-400/60 flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>

              {/* Title */}
              <div className="text-red-400 font-bold text-sm font-mono-ibm tracking-wide mb-2">
                EXPORT FAILED
              </div>

              {/* Error message — first meaningful line */}
              <div className="px-6 text-center max-w-[280px]">
                <div className="text-white/80 text-xs font-mono-ibm leading-relaxed">
                  {(() => {
                    const msg = exportError || '';
                    // Show first line (the summary), truncate if very long
                    const firstLine = msg.split('\n')[0] || 'An error occurred during export.';
                    return firstLine.length > 120 ? firstLine.slice(0, 117) + '...' : firstLine;
                  })()}
                </div>
                {/* Show detail lines if present */}
                {exportError && exportError.includes('\n') && (
                  <div className="mt-2 text-white/50 text-xs font-mono-ibm leading-relaxed">
                    {exportError.split('\n').slice(1, 3).map((line, i) => (
                      <div key={i}>{line.length > 80 ? line.slice(0, 77) + '...' : line}</div>
                    ))}
                  </div>
                )}
              </div>

              {/* Dismiss hint */}
              <div className="mt-4 text-xs font-mono-ibm" style={{ color: '#555' }}>
                CLICK TO DISMISS
              </div>
            </div>
          )}
        </div>

        {/* Status bar — WeatherDashboard device-label style */}
        <div className="flex items-center justify-between px-2.5 py-1 mt-1">
          <span className="device-section-label" style={{ fontSize: '8px', color: 'var(--device-text-dim)' }}>
            1080×1350 — 4:5
          </span>
          <span className="device-section-label" style={{ fontSize: '10px', color: 'var(--device-text)', letterSpacing: '2px' }}>
            PREVIEW
          </span>
          <span className="device-section-label" style={{ fontSize: '8px', color: '#444' }}>
            {template ? template.toUpperCase().replace(/-/g, ' ') : 'DEFAULT'}
          </span>
        </div>
      </div>
    </div>
  );
});
